import React, { useState, useEffect ,useCallback } from 'react';
import styled from 'styled-components';
import { Calendar, Menu, LayoutList, Server, X, ChevronDown ,Monitor } from 'lucide-react';
import OrdersMonitor from './OrdersMonitor/index.js';
import CurrentScheduledJobs from './OrdersMonitor/CurrentScheduledJobs'; // 방금 생성한 컴포넌트 import
// Styled Components
const Container = styled.div`
  display: flex;
  height: 100vh;
  background-color: #f3f4f6;
  font-size:12pt;
`;

const Sidebar = styled.div`
  background-color: white;
  width: 16rem;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transform: ${props => (props.isOpen ? 'translateX(0)' : 'translateX(-100%)')};
  transition: transform 0.3s ease-in-out;
  position: absolute;
  left: 0;
  z-index: 30;
  @media (min-width: 768px) {
    transform: translateX(0);
    position: static;
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1rem;
  overflow: hidden;
  font-size:9pt;
`;

const Button = styled.button`
  background-color: #3b82f6;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    background-color: #2563eb;
  }
`;

const TabButton = styled(Button)`
  background-color: ${props => (props.active ? '#3b82f6' : '#e5e7eb')};
  color: ${props => (props.active ? 'white' : 'black')};
  flex: 1;
  border-radius: 0;
`;


const LogContentContainer = styled.div`
  flex-grow: 1;
  overflow-y: auto;  // 로그 내용만 스크롤 가능하게 설정
  max-height: calc(100vh - 150px);  // 최대 높이를 설정해서 스크롤이 생기게 함 (150px은 위의 탭 및 제목 공간 확보)
  padding: 1rem;
  font-size:10pt;
`;

const TabContainer = styled.div`
  display: flex;
  position: sticky;  // 스크롤해도 탭이 고정됨
  top: 0;  // 화면의 최상단에 고정
  background-color: white;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const TitleContainer = styled.div`
  position: sticky;  // 제목도 고정되도록 설정
  top: 3rem;  // 탭 아래에 위치
  background-color: white;
  z-index: 9;
  padding: 1rem 0;
`;
const LogItem = styled.div`
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.5rem;
  margin-bottom: 0.5rem;
  font-family: monospace;
  font-size: 0.875rem;
  color: #6b7280;
  font-size:10pt;
`;
const DateSelectorContainer = styled.div`
  background-color: white;
  padding: 1rem;
  border-radius: 0.375rem;
  height:100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  display: flex;
  flex-direction: column;
`;
const Dropdown = styled.select`
  display: block;
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  appearance: none;
  background-color: white;
  cursor: pointer;
`;


const ListBoxContainer = styled.div`
  background-color: white;
  padding: 1rem;
  border-radius: 0.375rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow-y: auto;  /* 세로 스크롤 생성 */
  display: flex;
  flex-direction: column;
`;

const ListBoxItem = styled.div`
  width:100%;
  padding: 0.2rem;
  background-color: ${props => (props.active ? '#3b82f6' : '#f3f4f6')};
  color: ${props => (props.active ? 'white' : 'black')};
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  &:hover {
    background-color: #2563eb;
    color: white;
  }
  text-align: center;
`;

const SlideUpViewer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease-in-out;
  transform: ${props => (props.isVisible ? 'translateY(0)' : 'translateY(100%)')};
  height: 30%;
`;

const DateSelector = ({ onDateSelect, selectedDate }) => {
  const [currentYear, setCurrentYear] = useState(selectedDate.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(selectedDate.getMonth());
  const [currentDay, setCurrentDay] = useState(selectedDate.getDate());  // 선택한 날짜를 유지
  

  // 한국어 월 이름 배열
  const months = [
    '1월', '2월', '3월', '4월', '5월', '6월',
    '7월', '8월', '9월', '10월', '11월', '12월'
  ];

  // 현재 선택한 연도와 월에 따라 해당 월의 일수 계산
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const dateOptions = Array.from(
    { length: getDaysInMonth(currentYear, currentMonth) },
    (_, i) => new Date(currentYear, currentMonth, i + 1)
  );

  const handleYearChange = (year) => {
    setCurrentYear(year);
  };

  const handleMonthChange = (month) => {
    setCurrentMonth(month);
  };

  const handleDaySelect = (day) => {
    setCurrentDay(day);  // 선택한 날짜를 상태로 유지
    const selectedDate = new Date(currentYear, currentMonth, day);
    onDateSelect(selectedDate);  // 부모 컴포넌트로 선택한 날짜를 전달
  };

  useEffect(() => {
    onDateSelect(new Date(currentYear, currentMonth, currentDay));  // 현재 상태에 맞는 날짜 선택
  }, [currentYear, currentMonth, currentDay, onDateSelect]);

  return (
    <DateSelectorContainer>
      <div>
        <label>Year</label>
        <Dropdown value={currentYear} onChange={(e) => handleYearChange(parseInt(e.target.value))}>
          {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </Dropdown>
      </div>
      <div>
        <label>Month</label>
        <Dropdown value={currentMonth} onChange={(e) => handleMonthChange(parseInt(e.target.value))}>
          {months.map((month, index) => (
            <option key={month} value={index}>{month}</option>
          ))}
        </Dropdown>
      </div>
      <ListBoxContainer>
        {dateOptions.map((date) => (
          <ListBoxItem
            key={date.getDate()}
            active={date.getDate() === currentDay}
            onClick={() => handleDaySelect(date.getDate())}
          >
            {date.getDate()}일
          </ListBoxItem>
        ))}
      </ListBoxContainer>
    </DateSelectorContainer>
  );
};

  

// LogViewer component
const LogViewer = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());  // 날짜 기본값 유지
  const [activeTab, setActiveTab] = useState('frontend');
  const [logs, setLogs] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true); // 사이드바 초기 상태 열림으로 변경
  const [isLogViewerVisible, setIsLogViewerVisible] = useState(false);
  const [isMonitoringVisible, setIsMonitoringVisible] = useState(false); // 대행경매 모니터링 화면 토글 상태 추가

 
  const toggleMonitoring = () => {
    setIsMonitoringVisible(!isMonitoringVisible); // 대행경매 모니터링 화면 토글
  };
  const fetchLogs = async (date, tab) => {
    try {
      // 날짜를 로컬 시간대로 변환하여 YYYY-MM-DD 형식으로 보냄
      const formattedDate = date.getFullYear() + '-' + (date.getMonth() + 1).toString().padStart(2, '0') + '-' + date.getDate().toString().padStart(2, '0');
  
      const response = await fetch(`/request/get_log?date=${formattedDate}&tab=${tab}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
  
      if (!response.ok) {
        throw new Error('로그 데이터를 가져오는 중 오류 발생');
      }
  
      const data = await response.json();
      setLogs(data);  // 받아온 로그 데이터를 상태로 저장
    } catch (error) {
      console.error('로그 데이터 가져오기 실패:', error);
      setLogs([]);  // 오류 발생 시 빈 배열 설정
    }
  };
  useEffect(() => {
    // 데이터 패치 후 1일로 초기화되는 문제를 방지
    fetchLogs(selectedDate, activeTab);  // 선택된 날짜와 탭 상태에 맞춰 데이터 로드
  }, [activeTab]);  // 날짜 또는 탭 상태가 변경될 때마다 호출

  const handleDateSelect = useCallback((date) => {
    setSelectedDate(date);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <Container>
       <Sidebar isOpen={isSidebarOpen}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1>
              <Calendar /> Log Viewer
            </h1>
            <button onClick={toggleSidebar}>
              <X /> {/* 사이드바 숨기기 버튼 */}
            </button>
          </div>
          <DateSelector onDateSelect={handleDateSelect} selectedDate={selectedDate} />
        </Sidebar>

        <MainContent>
        <button onClick={toggleMonitoring}> {/* 기존 사이드바 버튼을 경매 모니터링으로 변경 */}
          <Monitor /> 경매 모니터링 화면
        </button>

        {/* 탭을 상단에 고정 */}
        {!isMonitoringVisible ?
        <TabContainer>
          <TabButton active={activeTab === 'frontend'} onClick={() => setActiveTab('frontend')}>
            <LayoutList /> Frontend Logs
          </TabButton>
          <TabButton active={activeTab === 'backend'} onClick={() => setActiveTab('backend')}>
            <Server /> Backend Logs
          </TabButton>
        </TabContainer>:''}
        {/* 로그 내용만 스크롤 가능 */}
        {!isMonitoringVisible ? ( // 모니터링 화면이 보이지 않으면 로그를 렌더링
          <LogContentContainer>
            {logs.map((log, index) => (
              <LogItem key={index}>
                <span>{'[' + new Date(log.created_at).toLocaleTimeString() + ']        '}</span>
                <span>{log.log_text}</span>
              </LogItem>
            ))}
          </LogContentContainer>
        ) : (
          <LogContentContainer>
            {/* 대행경매 모니터링 화면 내용 */}
            <CurrentScheduledJobs />
            <>
            <button onClick={async () => {
          try {
            const response = await fetch("/Acution/test-order", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
            });

            if (response.ok) {
              // 발권 후 다시 주문 내역을 불러옵니다.
            } else {
              alert('주문생성 완료');
            }
          } catch (error) {
            console.error("테스트 발권 요청 중 오류 발생:", error);
          }
        }}
      >
        테스트 주문생성</button>
        <button onClick={async () => {
          try {
            const response = await fetch("/Acution/test-order-request", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
            });

            if (response.ok) {
              // 발권 후 다시 주문 내역을 불러옵니다.
            } else {
              alert('주문생성 완료');
            }
          } catch (error) {
            console.error("테스트 발권 요청 중 오류 발생:", error);
          }
        }}
      >
        테스트 주문 입금처리</button>
        <button onClick={async () => {
          try {
            const response = await fetch("/Acution/test-delete-order", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
            });

            if (response.ok) {
              // 발권 후 다시 주문 내역을 불러옵니다.
            } else {
              alert('주문삭제 완료');
            }
          } catch (error) {
            console.error("테스트 발권 요청 중 오류 발생:", error);
          }
        }}
      >이력삭제</button>
               <button onClick={async () => {
          try {
            const response = await fetch("/Acution/test-ban-clear", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
            });

            if (response.ok) {
              const data = await response.json();
            } else {
              alert('초기화 완료');
            }
          } catch (error) {
            console.error("테스트 발권 요청 중 오류 발생:", error);
          }
        }}
      >벤 초기화</button>
            
            </>
            <OrdersMonitor/>
          </LogContentContainer>
        )}
      </MainContent>
      <SlideUpViewer isVisible={isLogViewerVisible}>
        <div>
          <h2>Debug Log Viewer</h2>
          <button onClick={() => setIsLogViewerVisible(false)}>
            <X />
          </button>
        </div>
        <div>
          <p>Debug logs will appear here...</p>
        </div>
      </SlideUpViewer>
    </Container>
  );
};

export default LogViewer;

// /  